/* eslint-disable react-refresh/only-export-components */
import * as Sentry from '@sentry/react';
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  // authDomain fixo no domínio canônico do Firebase: o handler de auth é servido pelo
  // próprio Google (sem proxy /__/auth, sem X-Frame-Options cross-origin, redirect URI já
  // registrado), e prod/staging ficam idênticos — staging reflete 100% produção (H0).
  // NÃO apontar para o domínio do app: quebra o login fora da origem de produção.
  authDomain: 'gri-oraculo.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ hd: 'griinstitute.org' });

export interface User {
  uid: string;
  email: string;
  name: string | null;
  photoURL: string | null;
  token: string;
  role: string | null;
  allowedPages: string[]; // páginas efetivas (do /me); controla menu e guards de rota
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function isGriEmail(email: string | null | undefined): boolean {
  return (email || '').toLowerCase().endsWith('@griinstitute.org');
}

async function toUser(firebaseUser: FirebaseUser): Promise<User> {
  const token = await firebaseUser.getIdToken();
  let role: string | null = null;
  let allowedPages: string[] = [];
  try {
    const res = await fetch('/api/users/me', { headers: { Authorization: 'Bearer ' + token } });
    if (res.ok) {
      const data = await res.json();
      role = data.role ?? null;
      allowedPages = Array.isArray(data.allowedPages) ? data.allowedPages : [];
    }
  } catch { /* silently ignore */ }
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email || '',
    name: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    token,
    role,
    allowedPages,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      console.log('[Auth] onAuthStateChanged:', firebaseUser ? firebaseUser.email : 'null');
      try {
        if (firebaseUser) {
          if (!isGriEmail(firebaseUser.email)) {
            await firebaseSignOut(auth);
            setUser(null);
          } else {
            const u = await toUser(firebaseUser);
            setUser(u);
            Sentry.setUser({ id: u.uid, email: u.email, username: u.name ?? u.email });
          }
        } else {
          setUser(null);
          Sentry.setUser(null);
        }
      } catch (err) {
        console.error('[Auth] onAuthStateChanged error:', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = async () => {
    console.log('[Auth] signIn → popup');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (!isGriEmail(result.user.email)) {
        console.warn('[Auth] email não autorizado, fazendo logout');
        await firebaseSignOut(auth);
      }
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      // popup fechado pelo usuário — ignorar silenciosamente
      if (e?.code !== 'auth/popup-closed-by-user' && e?.code !== 'auth/cancelled-popup-request') {
        console.error('[Auth] signIn error:', e?.code, e?.message);
        throw err;
      }
    }
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    setUser(null);
  };

  const getToken = async (): Promise<string | null> => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return null;
      return await currentUser.getIdToken();
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
